import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { AuthContext } from '../../../context/AuthContext';
import { Button } from '../../common/Button';

const ForgotPasswordContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  justify-content: center;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const AppTitle = styled.h1`
  font-size: 28px;
  color: ${theme.colors.deepNavy};
  margin-top: ${theme.spacing.md};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSizes.medium};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.deepNavy};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.brandYellow};
  }
`;

const Message = styled.p`
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.deepNavy};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`;

const ErrorMessage = styled.p`
  color: #c62828;
  font-size: ${theme.typography.fontSizes.small};
  margin-top: ${theme.spacing.xs};
`;

const BackToLoginLink = styled.p`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
`;

const Link = styled.span`
  color: blue;
  cursor: pointer;
`;

export const ForgotPasswordScreen = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false); // New loading state
  const { resetPassword } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true); // Set loading to true when submission starts
    try {
      await resetPassword(email);
      setMessage('If an account with that email exists, a password reset link has been sent to your inbox.');
      // Introduce a delay to make the loading state more noticeable
      await new Promise(resolve => setTimeout(resolve, 1500)); 
    } catch (err) {
      setError('Failed to send password reset email. Please check your email address.');
      console.error("Password reset error:", err);
    } finally {
      setLoading(false); // Set loading to false when submission ends
    }
  };

  return (
    <ForgotPasswordContainer>
      <Logo>
        <img src="/logo192.png" alt="Lightway Logo" width="80" />
        <AppTitle>Lightway</AppTitle>
      </Logo>

      <Message>
        Enter your email address below and we'll send you a link to reset your password.
      </Message>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
          />
        </FormGroup>
        
        {message && <Message>{message}</Message>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Button primary type="submit" disabled={loading}>
          {loading ? 'Sending...' : 'Reset Password'}
        </Button>
      </Form>

      <BackToLoginLink>
        Remember your password? <Link onClick={() => navigate('/login')}>Back to Log In</Link>
      </BackToLoginLink>
    </ForgotPasswordContainer>
  );
};
